import React, { useState, useEffect, useRef } from "react";
import {
  Container,
  Typography,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogContentText,
  LinearProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import { SurveyCreator, SurveyCreatorComponent } from "survey-creator-react";
import "survey-core/survey-core.css";
import "survey-creator-core/survey-creator-core.css";
import { useSearchParams, useNavigate } from "react-router-dom";
import CircularProgress from "@mui/material/CircularProgress";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import AddIcon from "@mui/icons-material/Add";
import { ROUTE_SURVEY_HOME } from "constants/routerPath";
import { useSurveyMutations, useToast } from "hooks/survey";
import { getSurvey, getSurveyByToken } from "api/cansurvey/surveyApi";
import { extractAndUploadSurveyImages } from "utils/survey/extractSurveyImages";

const CreateSurvey: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");
  const surveyId = searchParams.get("surveyId");
  const isNewSurveyMode = !surveyId && !token;

  const [creator, setCreator] = useState<SurveyCreator | null>(null);
  const [loading, setLoading] = useState(false);
  const [isSurveyLoaded, setIsSurveyLoaded] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [progress, setProgress] = useState(0);
  const progressTimerRef = useRef<NodeJS.Timeout | null>(null);
  const navigateTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const { createSurveyMutation, updateSurveyMutation, creating, updating } =
    useSurveyMutations();
  const { toast, showToast, closeToast } = useToast();

  useEffect(() => {
    const creatorInstance = new SurveyCreator({
      showOptions: false,
      showToolbox: false,
      showPropertyGrid: false,
      showJSONEditorTab: false,
      allowModifyPages: true,
      showLogicTab: false,
      showTestSurveyTab: false,
      showTranslationTab: false,
    });
    setCreator(creatorInstance);

    // If in update mode, fetch the survey content
    if (token || surveyId) {
      setLoading(true);
      const fetchSurvey = async () => {
        try {
          const surveyContent = token
            ? await getSurveyByToken(token)
            : await getSurvey(Number(surveyId));
          const parsedContent = JSON.parse(surveyContent);
          creatorInstance.JSON = parsedContent;
        } catch (error) {
          const errorMessage =
            error instanceof Error
              ? error.message
              : "Failed to load the survey. Please try again later.";
          setLoadError(true);
          showToast(errorMessage, "error");
        } finally {
          setLoading(false);
          setIsSurveyLoaded(true);
        }
      };

      fetchSurvey();
    } else {
      setIsSurveyLoaded(true);
    }
  }, [token, surveyId, showToast]);

  useEffect(() => {
    // Remove banner only after the survey is fully loaded
    // This DOM manipulation is required for SurveyJS
    if (isSurveyLoaded && creator) {
      const banner = document.querySelector(".svc-creator__banner");
      if (banner && banner.parentNode) {
        banner.parentNode.removeChild(banner);
      }
    }
  }, [isSurveyLoaded, creator]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (progressTimerRef.current) {
        clearInterval(progressTimerRef.current);
      }
      if (navigateTimeoutRef.current) {
        clearTimeout(navigateTimeoutRef.current);
      }
    };
  }, []);

  const handleSaveSurvey = async () => {
    if (!creator) {
      return;
    }

    try {
      const surveyJson = creator.JSON;

      // Validate survey has content
      if (!surveyJson || !surveyJson.pages || surveyJson.pages.length === 0) {
        showToast("Please add at least one page to your survey before saving.", "error");
        return;
      }

      // Extract the title, default to current date if not present
      const title =
        surveyJson && surveyJson.title
          ? surveyJson.title
          : new Date().toLocaleDateString();

      // Extract base64 images and upload to file bucket, replacing with URLs
      const { json: processedJson, errors: imageErrors } =
        await extractAndUploadSurveyImages(surveyJson);

      if (imageErrors.length > 0) {
        console.warn(
          `[CreateSurvey] ${imageErrors.length} image upload(s) failed:`,
          imageErrors
        );
        showToast("Some images could not be uploaded and were kept as-is.", "warning");
      }

      // Convert the processed JSON object to a string
      const jsonString = JSON.stringify(processedJson);

      if (isNewSurveyMode) {
        // Call the API to create the survey
        await createSurveyMutation(title, jsonString);

        // Show success dialog
        setDialogOpen(true);

        // Start progress animation
        setProgress(0);
        progressTimerRef.current = setInterval(() => {
          setProgress((oldProgress) => {
            if (oldProgress === 100) {
              if (progressTimerRef.current) {
                clearInterval(progressTimerRef.current);
              }
              navigateTimeoutRef.current = setTimeout(() => {
                setDialogOpen(false);
                navigate(ROUTE_SURVEY_HOME);
              }, 500);
              return 100;
            }
            return Math.min(oldProgress + 10, 100);
          });
        }, 100);
      } else {
        // Call the API to update the survey
        const identifier = token ? { token } : { surveyId: Number(surveyId) };
        await updateSurveyMutation(identifier, title, jsonString);
        navigate(ROUTE_SURVEY_HOME);
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to save the survey. Please try again later.";
      showToast(errorMessage, "error");
    }
  };

  const handleReturn = () => {
    navigate(ROUTE_SURVEY_HOME);
  };

  return (
    <Container
      disableGutters
      maxWidth="xl"
      sx={{
        height: "95vh",
        display: "flex",
        flexDirection: "column",
        px: 0,
      }}
    >
      <Box sx={{ flexGrow: 1, px: 0 }}>
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
            }}
          >
            <CircularProgress />
          </Box>
        ) : loadError ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="100vh"
          >
            <Typography>Failed to load the survey. Please try again later.</Typography>
          </Box>
        ) : creator ? (
          <>
            <SurveyCreatorComponent creator={creator} />
          </>
        ) : (
          <Typography>Loading Survey Creator...</Typography>
        )}
      </Box>

      <Box
        sx={{ display: "flex", justifyContent: "space-between", my: 1, mx: 1 }}
      >
        <Button
          onClick={handleReturn}
          variant="outlined"
          color="primary"
          startIcon={<ArrowBackIosNewIcon />}
          disabled={creating || updating}
        >
          Return
        </Button>

        <Button
          onClick={handleSaveSurvey}
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          disabled={creating || updating}
        >
          {creating || updating
            ? isNewSurveyMode
              ? "Creating..."
              : "Updating..."
            : isNewSurveyMode
            ? "Create Survey"
            : "Update Survey"}
        </Button>
      </Box>

      {/* Dialog for success message */}
      <Dialog open={dialogOpen} maxWidth="xs" fullWidth>
        <DialogContent>
          <DialogContentText align="center">
            Survey created successfully!
          </DialogContentText>
          <Box sx={{ mt: 2 }}>
            <LinearProgress variant="determinate" value={progress} />
          </Box>
        </DialogContent>
      </Dialog>

      {/* Snackbar for toast notifications */}
      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={closeToast}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={closeToast} severity={toast.severity} sx={{ width: "100%" }}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default CreateSurvey;
